import { MXInteractiveComponent, MXLookComponent, convertToPlacement, AXClosbaleComponent, AXCommonModule } from '@acorex/components/common';
import * as i0 from '@angular/core';
import { EventEmitter, Injectable, inject, Component, ViewChild, Output, HostListener, HostBinding, ChangeDetectionStrategy, ViewEncapsulation, Input, NgModule } from '@angular/core';
import * as i1 from '@acorex/components/popover';
import { AXPopoverComponent, AXPopoverModule } from '@acorex/components/popover';
import { AXPlatform, isBrowser } from '@acorex/core/platform';
import { classes } from 'polytype';
import { startWith } from 'rxjs';
import { AXDecoratorModule } from '@acorex/components/decorators';
import { A11yModule } from '@angular/cdk/a11y';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';

class MXDropdownBoxBaseComponent extends MXInteractiveComponent {
    constructor() {
        super(...arguments);
        this.onOpened = new EventEmitter();
        this.onClosed = new EventEmitter();
    }
    emitOnOpenedEvent() {
        this.onOpened.emit({
            component: this,
            htmlElement: this.getHostElement(),
        });
    }
    emitOnClosedEvent() {
        this.onClosed.emit({
            component: this,
            htmlElement: this.getHostElement(),
        });
    }
    toggle() {
        if (this.disabled !== true) {
            this.dropdown.toggle();
        }
    }
    close() {
        if (!this.isOpen || this.disabled) {
            return;
        }
        this.dropdown.close();
        this.focus();
    }
    open() {
        if (this.isOpen || this.disabled) {
            return;
        }
        this.dropdown.open();
        this.dropdown.focus();
    }
    get isOpen() {
        return this.dropdown?.isOpen;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: MXDropdownBoxBaseComponent, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: MXDropdownBoxBaseComponent }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: MXDropdownBoxBaseComponent, decorators: [{
            type: Injectable
        }] });

class AXDropdownBoxComponent extends classes(MXInteractiveComponent, MXLookComponent) {
    constructor() {
        super(...arguments);
        this._platform = inject(AXPlatform);
        this._isMobile = false;
        this._popoverWidth = '180px';
        this._placement = convertToPlacement('bottom-start');
        this.onOpened = new EventEmitter();
        this.onClosed = new EventEmitter();
    }
    ngOnInit() {
        super.ngOnInit();
        this._target = this.getHostElement();
        this._platform.resize.pipe(startWith()).subscribe(() => {
            this._isMobile = this._platform.is('SM');
            if (!this.isActionsheetStyle)
                this.close();
        });
    }
    _handlePopupOnOpened(e) {
        this._popover?.focus();
        this.onOpened.emit({
            component: this,
            isUserInteraction: e.isUserInteraction,
        });
    }
    _handlePopupOnClosed(e) {
        this.focus();
        this.onClosed.emit({
            component: this,
            isUserInteraction: e.isUserInteraction,
        });
    }
    _handleKeydown(e) {
        if (e.ctrlKey && e.code === 'ArrowDown') {
            this.open();
            e.preventDefault();
        }
        if (e.code === 'Escape') {
            this.close();
            e.preventDefault();
        }
    }
    updatePosition() {
        this._popover?.updatePosition();
    }
    toggle() {
        if (this.disabled !== true) {
            this._popover.toggle();
        }
    }
    close() {
        if (!this.isOpen || this.disabled) {
            return;
        }
        this._popover.close();
        this.focus();
    }
    open() {
        if (this.isOpen || this.disabled) {
            return;
        }
        this._popover.open();
        this._popover.focus();
    }
    get isOpen() {
        return this._popover?.isOpen;
    }
    get isActionsheetStyle() {
        return this._popover?.isActionsheetStyle;
    }
    get __hostClass() {
        return `ax-editor-container ax-look-${this.look} ${this.disabled ? 'ax-state-disabled' : ''}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXDropdownBoxComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.0.2", type: AXDropdownBoxComponent, selector: "ax-dropdown-box", inputs: { disabled: "disabled", look: "look" }, outputs: { disabledChange: "disabledChange", onBlur: "onBlur", onFocus: "onFocus", onClick: "onClick", onOpened: "onOpened", onClosed: "onClosed" }, host: { attributes: { "ngSkipHydration": "true" }, listeners: { "keydown": "_handleKeydown($event)" }, properties: { "class": "this.__hostClass" } }, viewQueries: [{ propertyName: "_popover", first: true, predicate: AXPopoverComponent, descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<ng-content select=\"[input]\"> </ng-content>\n<ax-popover [target]=\"_target\" [placement]=\"_placement\" [openOn]=\"'manual'\" [closeOn]=\"'clickOut'\"\n  [adaptivityEnabled]=\"true\" (onOpened)=\"_handlePopupOnOpened($event)\" (onClosed)=\"_handlePopupOnClosed($event)\"\n  #popover>\n  <div class=\"ax-overlay-pane\" [class.ax-w-full]=\"_isMobile\" aria-modal=\"true\" (keydown)=\"_handleKeydown($event)\"\n    [class.ax-overlay-actionsheet]=\"popover.isActionsheetStyle\">\n    <ng-content select=\"[panel]\"> </ng-content>\n  </div>\n</ax-popover>", dependencies: [{ kind: "component", type: i1.AXPopoverComponent, selector: "ax-popover", inputs: ["offsetX", "offsetY", "target", "placement", "content", "openOn", "closeOn", "hasBackdrop", "openAfter", "closeAfter", "backdropClass", "adaptivityEnabled"], outputs: ["onOpened", "onClosed"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXDropdownBoxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ax-dropdown-box', inputs: ['disabled', 'look'], outputs: ['disabledChange', 'onBlur', 'onFocus', 'onClick'], host: { ngSkipHydration: 'true' }, template: "<ng-content select=\"[input]\"> </ng-content>\n<ax-popover [target]=\"_target\" [placement]=\"_placement\" [openOn]=\"'manual'\" [closeOn]=\"'clickOut'\"\n  [adaptivityEnabled]=\"true\" (onOpened)=\"_handlePopupOnOpened($event)\" (onClosed)=\"_handlePopupOnClosed($event)\"\n  #popover>\n  <div class=\"ax-overlay-pane\" [class.ax-w-full]=\"_isMobile\" aria-modal=\"true\" (keydown)=\"_handleKeydown($event)\"\n    [class.ax-overlay-actionsheet]=\"popover.isActionsheetStyle\">\n    <ng-content select=\"[panel]\"> </ng-content>\n  </div>\n</ax-popover>" }]
        }], propDecorators: { _popover: [{
                type: ViewChild,
                args: [AXPopoverComponent, { static: true }]
            }], onOpened: [{
                type: Output
            }], onClosed: [{
                type: Output
            }], _handleKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], __hostClass: [{
                type: HostBinding,
                args: ['class']
            }] } });

/* eslint-disable @angular-eslint/no-host-metadata-property */
class AXDropdownPanelComponent extends MXInteractiveComponent {
    get _target() {
        return this.__target;
    }
    set _target(v) {
        this.__target = v;
    }
    // what the hell ???
    // constructor() {
    //   super();
    //   afterNextRender(() => {
    //     this._target = this.getHostElement()?.parentElement as HTMLDivElement;
    //     this._platform.resize.pipe(startWith(null)).subscribe(() => {
    //       this._isMobile = this._platform.is('SM');
    //       this._popover?.close();
    //     });
    //     [this._target, this.getHostElement()].forEach((el) => {
    //       el.addEventListener('keydown', (e) => {
    //         if (e.code == 'Escape' && this.isOpen) {
    //           e.stopPropagation();
    //           this.close();
    //         }
    //       });
    //     });
    //   });
    // }
    constructor() {
        super();
        this._platform = inject(AXPlatform);
        this._isMobile = false;
        this._placement = convertToPlacement('bottom-start');
        this._subs = [];
        this.onOpened = new EventEmitter();
        this.onClosed = new EventEmitter();
        this.adaptivityEnabled = false;
    }
    ngOnInit() {
        if (isBrowser()) {
            super.ngOnInit();
            if (!this._target) {
                this._target = this.getHostElement()?.parentElement;
            }
            this._platform.resize.pipe(startWith(null)).subscribe(() => {
                this._isMobile = this._platform.is('SM');
                this._popover?.close();
            });
        }
    }
    ngAfterViewInit() {
        [this._target, this.getHostElement()].forEach((el) => {
            el?.addEventListener('keydown', (e) => {
                if (e.code == 'Escape' && this.isOpen) {
                    e.stopPropagation();
                    this.close();
                }
            });
        });
    }
    ngOnDestroy() {
        this._subs.forEach((s) => {
            s?.unsubscribe();
        });
    }
    _handlePopupOnOpened(e) {
        this._popover?.focus();
        this.onOpened.emit({
            component: this,
            isUserInteraction: e.isUserInteraction,
        });
    }
    _handlePopupOnClosed(e) {
        this._target.focus();
        this.onClosed.emit({
            component: this,
            isUserInteraction: e.isUserInteraction,
        });
    }
    toggle() {
        if (this.disabled !== true) {
            this._popover.toggle();
        }
    }
    close() {
        if (!this.isOpen || this.disabled) {
            return;
        }
        this._popover.close();
        this.focus();
    }
    open() {
        if (this.isOpen || this.disabled) {
            return;
        }
        this._popover.open();
        this._popover.focus();
    }
    get isOpen() {
        return this._popover?.isOpen;
    }
    get isActionsheetStyle() {
        return this._popover?.isActionsheetStyle;
    }
    get __hostClass() {
        return ['ax-dropdown-panel'];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXDropdownPanelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.0.2", type: AXDropdownPanelComponent, selector: "ax-dropdown-panel", inputs: { isOpen: "isOpen", fitParent: "fitParent", dropdownWidth: "dropdownWidth", position: "position", _target: "_target", adaptivityEnabled: "adaptivityEnabled" }, outputs: { onOpened: "onOpened", onClosed: "onClosed" }, host: { attributes: { "ngSkipHydration": "true" }, properties: { "class": "this.__hostClass" } }, providers: [
            {
                provide: AXClosbaleComponent,
                useExisting: AXDropdownPanelComponent,
            },
        ], viewQueries: [{ propertyName: "_popover", first: true, predicate: AXPopoverComponent, descendants: true }], usesInheritance: true, ngImport: i0, template: "<ax-popover\n  [target]=\"_target\"\n  [placement]=\"_placement\"\n  [openOn]=\"'toggle'\"\n  [closeOn]=\"'clickOut'\"\n  [adaptivityEnabled]=\"adaptivityEnabled\"\n  (onOpened)=\"_handlePopupOnOpened($event)\"\n  (onClosed)=\"_handlePopupOnClosed($event)\"\n>\n  <div class=\"ax-overlay-pane\">\n    <ng-content> </ng-content>\n  </div>\n</ax-popover>\n", styles: [".ax-actionsheet-base .ax-overlay-pane{width:100%}\n"], dependencies: [{ kind: "component", type: i1.AXPopoverComponent, selector: "ax-popover", inputs: ["offsetX", "offsetY", "target", "placement", "content", "openOn", "closeOn", "hasBackdrop", "openAfter", "closeAfter", "backdropClass", "adaptivityEnabled"], outputs: ["onOpened", "onClosed"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXDropdownPanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ax-dropdown-panel', inputs: ['isOpen', 'fitParent', 'dropdownWidth', 'position'], outputs: ['onOpened', 'onClosed'], changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, providers: [
                        {
                            provide: AXClosbaleComponent,
                            useExisting: AXDropdownPanelComponent,
                        },
                    ], host: { ngSkipHydration: 'true' }, template: "<ax-popover\n  [target]=\"_target\"\n  [placement]=\"_placement\"\n  [openOn]=\"'toggle'\"\n  [closeOn]=\"'clickOut'\"\n  [adaptivityEnabled]=\"adaptivityEnabled\"\n  (onOpened)=\"_handlePopupOnOpened($event)\"\n  (onClosed)=\"_handlePopupOnClosed($event)\"\n>\n  <div class=\"ax-overlay-pane\">\n    <ng-content> </ng-content>\n  </div>\n</ax-popover>\n", styles: [".ax-actionsheet-base .ax-overlay-pane{width:100%}\n"] }]
        }], ctorParameters: () => [], propDecorators: { _target: [{
                type: Input
            }], _popover: [{
                type: ViewChild,
                args: [AXPopoverComponent]
            }], onOpened: [{
                type: Output
            }], onClosed: [{
                type: Output
            }], adaptivityEnabled: [{
                type: Input
            }], __hostClass: [{
                type: HostBinding,
                args: ['class']
            }] } });

class AXDropdownModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXDropdownModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.2", ngImport: i0, type: AXDropdownModule, declarations: [AXDropdownPanelComponent, AXDropdownBoxComponent], imports: [CommonModule, AXCommonModule, A11yModule, OverlayModule, AXDecoratorModule, AXPopoverModule], exports: [AXDropdownPanelComponent, AXDropdownBoxComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXDropdownModule, imports: [CommonModule, AXCommonModule, A11yModule, OverlayModule, AXDecoratorModule, AXPopoverModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXDropdownModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, AXCommonModule, A11yModule, OverlayModule, AXDecoratorModule, AXPopoverModule],
                    exports: [AXDropdownPanelComponent, AXDropdownBoxComponent],
                    declarations: [AXDropdownPanelComponent, AXDropdownBoxComponent],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AXDropdownBoxComponent, AXDropdownModule, AXDropdownPanelComponent, MXDropdownBoxBaseComponent };
